import React from 'react';

import * as converter from 'components/features/utils/converter';
import * as consts from 'config/consts';
import {
  addSignCommaFormat,
  commaFormat,
  getSign,
} from '../new-report/utils/formatter';
import { productTypesToUnit } from '../utils/productTypes';

type SymbolPositionitemProps = {
  isSubItem: boolean;
  isMargin: boolean;
  productType: number;
  title: string;
  valuation: number | null;
  quantity: number | null;
  profitOrLoss: number | null;
  className?: string;
};

type BrokerPositionItemProps = {
  brokerName: JSX.Element;
  sum: {
    valuation: number | null;
    quantity: number | null;
    profitOrLoss: number | null;
    productType: number;
  };
  items: {
    valuation: number | null;
    quantity: number | null;
    profitOrLoss: number | null;
    side: number;
    cashMargin: number;
    productType: number;
  }[];
};

const SymbolPositionItem = ({
  isSubItem,
  isMargin,
  productType,
  profitOrLoss,
  quantity,
  title,
  valuation,
  className,
}: SymbolPositionitemProps): React.ReactElement => {
  const profitOrLossColor = (sign => {
    switch (sign) {
      case 'plus':
      case 'zero':
        return 'text-upcolor';
      case 'minus':
        return 'text-downcolor';
    }
    return 'text-upcolor';
  })(getSign(profitOrLoss ?? 0));
  return (
    <>
      <div
        className={`${isSubItem ? 'ml-4' : ''} ${className ??
          ''} flex flex-col p-4`}>
        <div className="flex flex-row justify-between items-center">
          <div
            className={`${
              isSubItem
                ? 'font-normal text-bluegray-700'
                : 'font-bold text-bluegray-500'
            } text-base`}>
            {!isSubItem && isMargin ? null : title}
          </div>
          {!isMargin ? (
            <div
              className={`${
                isSubItem ? 'text-base font-normal' : 'text-lg font-bold'
              } text-bluegray-700`}>
              {valuation === null
                ? '---'
                : commaFormat(valuation, { numberOfDecimals: 0 })}{' '}
              円
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="mt-1 flex flex-row justify-between items-center">
          <div
            className={`${
              isSubItem ? 'text-sm' : 'text-base'
            } text-bluegray-700`}>
            {quantity === null
              ? '---'
              : commaFormat(quantity, { numberOfDecimals: 0 })}{' '}
            {productTypesToUnit(productType)}
          </div>
          <div className={`flex flex-row items-center`}>
            <div
              className={`${
                isSubItem ? 'text-xs' : 'text-sm'
              } mr-4 text-bluegray-500`}>
              評価損益
            </div>
            <div
              className={`${profitOrLossColor} ${
                isSubItem ? 'text-sm' : 'text-base'
              }`}>
              {addSignCommaFormat(profitOrLoss, { numberOfDecimals: 0 })} 円
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const BrokerPositionItem = ({
  brokerName,
  items,
  sum,
}: BrokerPositionItemProps): React.ReactElement => {
  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center bg-gray-300 text-bluegray-500 px-3 py-2 text-base">
          {brokerName}
        </div>
        <SymbolPositionItem
          {...{
            isSubItem: false,
            isMargin: items.every(
              item => item.cashMargin === consts.CashMargin.信用
            ),
            productType: sum.productType,
            profitOrLoss: sum.profitOrLoss,
            quantity: sum.quantity,
            title: '評価額',
            valuation: sum.valuation,
          }}
          className="border-b border-bluegray-300"
        />
        {items.map((item, index, arr) => {
          return (
            <SymbolPositionItem
              {...{
                isSubItem: true,
                isMargin: item.cashMargin === consts.CashMargin.信用,
                ...item,
                title: `${converter.getCashMarginName(
                  item.cashMargin
                )}${converter.getSideName(item.side)}`,
                key: index,
              }}
              className={`${
                index !== arr.length - 1 ? 'border-b border-bluegray-300' : ''
              }`}
            />
          );
        })}
      </div>
    </>
  );
};
